import { Box, Typography } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { ContainerPage, TitlePage } from "components";
import { Paginate } from "components/Paginate";
import SelectFilterMenu from "components/SelectFilterMenu";
import { usePopup } from "hooks/usePopup";
import { useEffect, useMemo, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import batchConferenceService from "services/bachConferenceService";
import { Search } from "shared/Search";
import { useDebaunce } from "utils";

import { childrenColumns, columns } from "./columns";
import { ModalUpdate } from "./components/ModalUpdate";
import { ReceiptConferenceTable } from "./components/ReceiptConferenceTable";
import { BoxPaginateContent, useStyles } from "./styles";

export const ReceiptConference = () => {
  const toDayDate = new Date();
  const [search, setSearch] = useState("");
  const [searchDateFrom, setSearchDateFrom] = useState(
    new Date(toDayDate.setHours(0, 0, 0, 1))
  );
  const [searchDateTo, setSearchDateTo] = useState(() => {
    return new Date(toDayDate.setHours(23, 59, 59, 0));
  });
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [selectedStatusId, setSelectedStatusId] = useState(null);
  const [conferenceData, setConferenceData] = useState({});
  const [conferenceStatus, setConferenceStatus] = useState([]);
  const [batchConferenceData, setBatchConferenceData] = useState([]);
  const [loading, setLoading] = useState(false);

  const debaunceSearch = useDebaunce({ fn: handleSearch, delay: 800 });
  const classes = useStyles();
  const { addPopup } = usePopup();

  function setPageToInitial() {
    if (page !== 1) {
      setPage(1);
    }
  }

  function handleSearch(event) {
    const { value } = event.target;

    // O unstable_batchedUpdates é usado para garantir que as alterações de estado aconteçam
    // em um único ciclo de renderização
    unstable_batchedUpdates(() => {
      if (page !== 1) {
        setPage(1);
      }
      setSearch(value);
    });
  }

  const toggleModal = () => setIsOpenModal(state => !state);

  const handleOpenModal = data => {
    toggleModal();
    setConferenceData(data);
  };

  const handleChangeDate = date => {
    const dateFrom = new Date(date.setHours(0, 0, 0, 1));
    const dateTo = new Date(date.setHours(23, 59, 59, 1));

    setPageToInitial();
    setSearchDateFrom(dateFrom);
    setSearchDateTo(dateTo);
  };

  const handleGetData = async () => {
    setLoading(true);
    try {
      const { data } = await batchConferenceService.getBatchesConference({
        page,
        statusId: selectedStatusId,
        search,
        searchDateFrom,
        searchDateTo
      });

      setBatchConferenceData(data.results);
      setTotalPages(data.pageSize);
    } catch {
      addPopup({
        type: "error",
        title: "Erro ao buscar dados"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusData = async () => {
    try {
      const { data } = await batchConferenceService.getConferenceStatus();

      setConferenceStatus(data);
    } catch {
      addPopup({
        type: "error",
        title: "Erro ao receber dados dos status."
      });
    }
  };

  const handleChangePage = page => {
    const anchor = document.querySelector("#back-to-top-anchor");
    if (anchor) {
      anchor.scrollIntoView({
        behavior: "smooth"
      });
    }
    setPage(page);
  };

  const formattedBatchConferenceData = useMemo(() => {
    return batchConferenceData.reduce((acc, batchConference) => {
      const batchInfos = batchConference.batch;
      const sellerId = batchConference.batch.seller.id;
      const seller = acc[sellerId] || {};
      const returnedQuantity = batchInfos.returned.reduce((acc, returned) => {
        return acc + returned?.quantity ?? 0;
      }, 0);
      const wasteQuantity = batchInfos.waste.reduce((acc, waste) => {
        return acc + waste?.quantity ?? 0;
      }, 0);
      const received = batchInfos.quantity + returnedQuantity + wasteQuantity;
      const receiptDate = new Date(
        batchInfos.receipt_date
      ).toLocaleDateString();
      const receiptTime = new Date(batchInfos.receipt_date).toLocaleTimeString(
        [],
        { hour: "2-digit", minute: "2-digit" }
      );

      acc[sellerId] = {
        conferences: [
          ...(seller.conferences?.length ? seller.conferences : []),
          {
            batchInfos: batchInfos,
            batchConferenceId: batchConference.id,
            quantityInvoice: batchConference.quantity_invoice,
            quantity_invoice: batchConference.quantity_invoice,
            observation: batchConference.observation,
            invoice: batchConference.url_invoice,
            invoice_number: batchConference.invoice_number,
            invoiceNumber: batchConference.invoice_number,
            invoice_issue_date: batchConference.invoice_issue_date,
            invoiceIssueDate: batchConference.invoice_issue_date,
            received,
            forecast: batchInfos.purchase?.prediction,
            returnedQuantity,
            wasteQuantity,
            receiptTime,
            status: batchConference.status
          }
        ],
        seller: batchInfos.seller,
        receiptDate
      };

      return acc;
    }, []);
  }, [batchConferenceData]);

  useEffect(() => {
    getStatusData();
  }, []);

  useEffect(() => {
    handleGetData();
  }, [selectedStatusId, searchDateFrom, searchDateTo, search, page]);

  return (
    <ContainerPage id="back-to-top-anchor">
      <TitlePage>Conferência de recebimento</TitlePage>
      <Box className={classes.boxAlign}>
        <Search
          onChange={debaunceSearch}
          placeholder="Pesquisar"
          variant="outlined"
          className={classes.input}
        />
        <Box className={classes.boxAlign}>
          <KeyboardDatePicker
            disableToolbar
            format="dd/MM/yyyy"
            margin="normal"
            value={searchDateFrom}
            onChange={handleChangeDate}
            className={classes.dateField}
            cancelLabel="Cancelar"
            InputProps={{
              readOnly: true
            }}
          />
        </Box>
        <Box
          style={{ gap: "1rem", marginLeft: "0.5rem" }}
          className={classes.boxAlign}
        >
          <Typography className={classes.filterText}>Filtrar:</Typography>
          <SelectFilterMenu
            onChange={statusId => {
              setPageToInitial();
              setSelectedStatusId(statusId);
            }}
            selectFilterProps={{
              value: selectedStatusId,
              options: [
                ...conferenceStatus.map(status => ({
                  id: status.id,
                  label: status.name,
                  color:
                    status.id === 1
                      ? { background: "#FFF3CD", label: "#C98400" }
                      : { background: "#D1E7DD", label: "#0A892E" }
                }))
              ]
            }}
          />
        </Box>
      </Box>

      <ReceiptConferenceTable
        columns={columns}
        childrenColumns={childrenColumns}
        data={Object.values(formattedBatchConferenceData)}
        setConferenceData={handleOpenModal}
        loading={loading}
      />

      <BoxPaginateContent>
        {totalPages > 1 && (
          <Paginate
            breakLabel="..."
            nextLabel={<AiOutlineRight />}
            forcePage={page - 1}
            onPageChange={e => handleChangePage(e.selected + 1)}
            pageRangeDisplayed={3}
            pageCount={totalPages}
            previousLabel={<AiOutlineLeft />}
            renderOnZeroPageCount={null}
          />
        )}
      </BoxPaginateContent>

      <ModalUpdate
        isOpen={isOpenModal}
        close={toggleModal}
        conferenceStatus={conferenceStatus}
        conferenceData={conferenceData}
        setPageToInitial={setPageToInitial}
        refreshData={handleGetData}
      />
    </ContainerPage>
  );
};
